import React, { ReactElement, useEffect, useState } from "react";
import {
    FormFieldReplacementInput, useGetDynamicOptionsQuery, FormFieldOption, FieldTypedValueInput, FormFieldOptionSettings, FormFieldType,
} from "../../../__generated__/api";
import EditableControl, { ControlType, RecursiveOption, ValueTypes } from "../../editable-text/EditableText";

export interface DynamicPickerProps<TIsRequired=false> {
    placeholder?: string;
    value: ValueTypes;
    parentValues?: ValueTypes[];
    dynamicChoiceContext: string;
    optionsSettings?: FormFieldOptionSettings;
    integrationId: string;
    type: ControlType;
    isRequired: TIsRequired | undefined;
    labels: string[] | undefined;
    onChange?: (
        newString: ValueTypes | (TIsRequired extends false ? undefined : never),
        replacements?: FormFieldReplacementInput[],
        displayText?: string,
        dynamicChoice?: string
    ) => void;

    getCurrentForm?: () => FieldTypedValueInput[];
}

const FORCE_RERENDER_TYPES = new Set([
    FormFieldType.AsyncDropdown,
    FormFieldType.Dropdown,
    FormFieldType.SearchText,
    FormFieldType.MultiLevelDropdown,
    FormFieldType.MultiSelectDropdown,
]);

function DynamicPicker<TIsRequired extends boolean = false>({
    onChange, placeholder, value, type, isRequired, parentValues,
    dynamicChoiceContext, integrationId, labels, getCurrentForm, optionsSettings,
}: DynamicPickerProps<TIsRequired>): ReactElement {
    const [options, setOptions] = useState<RecursiveOption<FormFieldOption>[]>([]);
    const currentValues = (getCurrentForm?.() ?? []).filter(formField => FORCE_RERENDER_TYPES.has(formField.type));
    const { loading: isLoading, data } = useGetDynamicOptionsQuery({
        variables: {
            filters: {
                context: dynamicChoiceContext,
                integrationId,
                value: parentValues as string[],
                currentValues,
            },
        },
    });

    useEffect(() => {
        if (!data?.workflowServiceChoiceValues?.options) {
            return;
        }
        setOptions(data?.workflowServiceChoiceValues?.options.map(row => ({
            label: row.label,
            value: row.value as string,
            data: row,
            children: row.children?.map(child => ({
                label: child.label,
                value: child.value as string,
                data: child,
            })),
        })) ?? []);
    }, [data?.workflowServiceChoiceValues?.options]);

    if (isLoading && options.isEmpty) {
        return (
            <div>Loading...</div>
        );
    }

    if (options.isEmpty && optionsSettings?.emptyOptionsMessage) {
        return (
            <div className="text-light my-2 border rounded p-2">{optionsSettings?.emptyOptionsMessage}</div>
        );
    }

    return (
        <EditableControl
            options={options}
            placeholder={placeholder}
            isRequired={isRequired}
            labels={labels}
            type={type}
            isEditing
            value={value}
            integrationId={integrationId}
            onChange={(newValue, replacements, text) => {
                const optionValue = Array.isArray(newValue) ? newValue.lastElement : newValue;
                const selectedOption = options.find(option => option.value === optionValue);
                onChange?.(
                    newValue as Parameters<typeof onChange>[0],
                    replacements,
                    text,
                    selectedOption?.data?.dynamicChoiceContext as string | undefined,
                );
            }}
        />
    );
}

DynamicPicker.defaultProps = {
    placeholder: undefined,
    onChange: undefined,
    parentValues: undefined,
    getCurrentForm: undefined,
    optionsSettings: undefined,
};

export default DynamicPicker as <
    TIsRequired extends boolean = false
> (
    props: DynamicPickerProps<TIsRequired>
) => ReturnType<typeof DynamicPicker>;
